'use client';

import { useTransition, useRef, useState } from 'react';
import { Stepper } from '@pixum/stepper';
import { useCartStore, useCartErrorStore } from 'stores/cart';
import { useImageStateStore } from 'stores/express-designer';
import { deleteArticleAction } from '../../../../../actions/overview/deleteArticleAction';
import { updateArticleQuantityAction } from '../../../../../actions/overview/updateArticleQuantityAction';
import {
  ARTICLE_ACTION_MODE,
  MAX_CART_ARTICLE_QUANTITY,
  type ArticleActionMode
} from '../../../../../constants/article';
import { ArticleDeleteDialog } from '../ArticleDeleteDialog/ArticleDeleteDialog';
import type { ArticleDeleteProps } from '../../../../../types/article';

export interface ArticleStepperProps {
  articleId: number;
  quantity: number;
  minQuantity?: number;
  maxQuantity?: number;
  mode: ArticleActionMode;
  deleteProps: ArticleDeleteProps;
}

const CART_QUANTITY_INPUT_PATTERN = /^\d*$/;

/**
 * ArticleStepper logic wrapper. It binds the Stepper component to the cart store and handles optimistic updates, network requests and deletion at the minimum quantity.
 *
 * @param props - The props for the ArticleStepper component, including articleId, quantity, minQuantity, maxQuantity, mode, and deleteProps.
 * @returns A Stepper component that allows users to increase or decrease the quantity of an article in the cart, with optimistic UI updates and error handling.
 */
export function ArticleStepper(props: ArticleStepperProps) {
  const { articleId, quantity, minQuantity, maxQuantity, mode, deleteProps } = props;
  const { articleTitle, previewUrl, previewAlt, metadataPromise, constants } = deleteProps;
  const startQuantityMutation = useCartStore(s => s.startQuantityMutation);
  const endQuantityMutation = useCartStore(s => s.endQuantityMutation);
  const setCartLiveValues = useCartStore(s => s.setCartLiveValues);
  const setArticleQuantity = useCartStore(s => s.setArticleQuantity);
  const addDeletedArticleId = useCartStore(s => s.addDeletedArticleId);
  const setCartError = useCartErrorStore(s => s.setError);
  const removeProductByArticleId = useImageStateStore(s => s.removeProductByArticleId);

  // Value shown in the stepper: set on click, reverted to confirmedQuantityRef on error.
  const [displayQuantity, setDisplayQuantity] = useState(quantity);
  // Last server-confirmed quantity, used to revert on error.
  const confirmedQuantityRef = useRef<number>(quantity);
  const [, startTransition] = useTransition();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // Own transition so the dialog's loading state ignores quantity updates.
  const [isDeletePending, startDeleteTransition] = useTransition();

  // Latest value the user clicked.
  const latestTargetQuantity = useRef<number>(quantity);
  // Set before startTransition so a rapid second click cannot start a concurrent request.
  const isNetworkProcessing = useRef<boolean>(false);

  if (mode === ARTICLE_ACTION_MODE.READ_ONLY) {
    return <span>{quantity}</span>;
  }

  const min = minQuantity ?? 1;
  const max = maxQuantity ?? MAX_CART_ARTICLE_QUANTITY;

  const handleQuantityChange = (newQuantity: number) => {
    const clampedQuantity = Math.min(max, Math.max(min, newQuantity));

    // Update target and UI immediately; the network call follows.
    latestTargetQuantity.current = clampedQuantity;
    setDisplayQuantity(clampedQuantity);
    setArticleQuantity(articleId, clampedQuantity);

    // A request is already running; its loop picks up the new target.
    if (isNetworkProcessing.current) return;

    isNetworkProcessing.current = true;

    startTransition(async () => {
      startQuantityMutation({ articleId, topic: 'quantity' });

      try {
        // Send sequentially until the confirmed quantity matches the latest target.
        while (true) {
          const currentSendingQty = latestTargetQuantity.current;
          const result = await updateArticleQuantityAction(articleId, currentSendingQty);

          if (!result.success) {
            setCartError(result.error.message);
            latestTargetQuantity.current = confirmedQuantityRef.current;
            setDisplayQuantity(confirmedQuantityRef.current);
            setArticleQuantity(articleId, confirmedQuantityRef.current);
            break;
          }

          confirmedQuantityRef.current = currentSendingQty;
          setCartLiveValues(result.data);

          // No newer click arrived, so we are done.
          if (latestTargetQuantity.current === currentSendingQty) break;
        }
      } catch {
        setCartError('i18n_cart_quantity_update_error');
        latestTargetQuantity.current = confirmedQuantityRef.current;
        setDisplayQuantity(confirmedQuantityRef.current);
        setArticleQuantity(articleId, confirmedQuantityRef.current);
      } finally {
        isNetworkProcessing.current = false;
        endQuantityMutation();
      }
    });
  };

  const handleInputChange = (newQuantity: number) => {
    if (!Number.isInteger(newQuantity) || newQuantity < min || newQuantity > max) {
      return;
    }

    handleQuantityChange(newQuantity);
  };

  const handleRemove = () => {
    // An in-flight quantity update would race the delete.
    if (isNetworkProcessing.current) return;

    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirmation = () => {
    /*
     * updateTag in the Server Action bundles the re-rendered article list (RSC payload)
     * into the same response as the action result.
     * isDeletePending stays true until that payload is applied, and the dialog closes after it.
     */
    startDeleteTransition(async () => {
      try {
        const result = await deleteArticleAction(articleId);

        if (!result.success) {
          setCartError(result.error.message);

          return;
        }

        if (result.data) {
          setCartLiveValues(result.data);
        }
        addDeletedArticleId(articleId); // hides the card even when no summary data is returned
        removeProductByArticleId(articleId);

        setIsDeleteDialogOpen(false);
      } catch {
        // Rejected action: close the dialog so the error snackbar is visible.
        setCartError('i18n_cart_delete_article_error');
        setIsDeleteDialogOpen(false);
      }
    });
  };

  const removeAriaLabel =
    constants['i18n_a11y_cart_delete_article']?.replace('{title}', articleTitle) ?? `Delete ${articleTitle} from cart`;

  return (
    <>
      <Stepper
        id={`stepper-${articleId}`}
        value={displayQuantity}
        onIncrease={() => handleQuantityChange(latestTargetQuantity.current + 1)}
        onDecrease={() => handleQuantityChange(latestTargetQuantity.current - 1)}
        onInputChange={handleInputChange}
        inputPattern={CART_QUANTITY_INPUT_PATTERN}
        onRemove={handleRemove}
        min={min}
        max={max}
        showRemoveAtOne
        textConstants={{
          decreaseAriaLabel: constants['i18n_cart_decrease_quantity'],
          increaseAriaLabel: constants['i18n_cart_increase_quantity'],
          quantityAriaLabel: constants['i18n_cart_product_quantity'],
          removeAriaLabel
        }}
      />
      <ArticleDeleteDialog
        isOpen={isDeleteDialogOpen}
        onConfirm={handleDeleteConfirmation}
        onCancel={() => setIsDeleteDialogOpen(false)}
        articleTitle={articleTitle}
        previewUrl={previewUrl}
        previewAlt={previewAlt}
        metadataPromise={metadataPromise}
        constants={constants}
        isLoading={isDeletePending}
      />
    </>
  );
}
